import { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Loader2, ShieldAlert, ShieldCheck, AlertCircle } from 'lucide-react'
import { pollUntilDone } from '../lib/api'
import type { StatusResponse, ScanStatus } from '../types/scan'
import styles from './ScanningPage.module.css'

const STEP_LABELS: Record<ScanStatus, string> = {
  pending:     'Oczekiwanie na start...',
  uploading:   'Przesyłanie pliku...',
  decompiling: 'Dekompilacja JAR przez CFR...',
  analyzing:   'Analiza przez modele AI...',
  aggregating: 'Obliczanie wyniku zbiorczego...',
  complete:    'Analiza zakończona',
  failed:      'Analiza nie powiodła się',
}

const STATUS_PROGRESS: Record<ScanStatus, number> = {
  pending:     5,
  uploading:   15,
  decompiling: 30,
  analyzing:   60,
  aggregating: 90,
  complete:    100,
  failed:      0,
}

const ANALYZING_STEPS = [
  'Wysyłanie kodu do Kimi K3...',
  'Wysyłanie kodu do Llama 3.2 90B...',
  'Oczekiwanie na odpowiedzi modeli...',
  'Modele analizują podejrzane wzorce...',
  'Weryfikacja wskaźników zagrożeń...',
  'Finalny przegląd wyników...',
]

export default function ScanningPage() {
  const { scanId } = useParams<{ scanId: string }>()
  const navigate = useNavigate()

  const [status, setStatus] = useState<StatusResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [displayStep, setDisplayStep] = useState<string>('Inicjalizacja...')
  const [analyzingIdx, setAnalyzingIdx] = useState(0)
  const analyzeTimer = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (status?.status === 'analyzing') {
      analyzeTimer.current = setInterval(() => {
        setAnalyzingIdx((i) => (i + 1) % ANALYZING_STEPS.length)
      }, 3000)
    } else {
      if (analyzeTimer.current) clearInterval(analyzeTimer.current)
    }
    return () => { if (analyzeTimer.current) clearInterval(analyzeTimer.current) }
  }, [status?.status])

  useEffect(() => {
    if (status?.status === 'analyzing') setDisplayStep(ANALYZING_STEPS[analyzingIdx])
  }, [analyzingIdx, status?.status])

  useEffect(() => {
    if (!scanId) { navigate('/'); return }

    pollUntilDone(
      scanId,
      (s) => {
        setStatus(s)
        if (s.status !== 'analyzing') setDisplayStep(s.currentStep ?? STEP_LABELS[s.status])
      },
      2000
    )
      .then(() => navigate(`/results/${scanId}`))
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Nieznany błąd'))
  }, [scanId, navigate])

  const progress = status?.progress ?? STATUS_PROGRESS[status?.status ?? 'pending'] ?? 5
  const isFailed = status?.status === 'failed' || error

  return (
    <div className={styles.root}>
      <div className={styles.card}>
        <div className={styles.iconWrapper}>
          {isFailed ? (
            <ShieldAlert className={styles.iconFailed} />
          ) : status?.status === 'complete' ? (
            <ShieldCheck className={styles.iconComplete} />
          ) : (
            <div className={styles.iconSpinWrapper}>
              <Loader2 className={`${styles.iconSpin} animate-spin`} />
            </div>
          )}
        </div>

        <h1 className={styles.title}>
          {isFailed
            ? 'Analiza nie powiodła się'
            : status?.status === 'complete'
            ? 'Analiza zakończona'
            : 'Trwa analiza pliku JAR'}
        </h1>

        <p className={styles.scanId}>ID skanu: {scanId}</p>

        {!isFailed && (
          <>
            <div
              className={styles.progressTrack}
              role="progressbar"
              aria-valuenow={progress}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div
                className={`${styles.progressFill} ${status?.status === 'analyzing' ? styles.progressAnimated : ''}`}
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className={styles.progressLabel}>
              <span>{displayStep}</span>
              <span>{progress}%</span>
            </div>
          </>
        )}

        {/* ── Oś czasu ────────────────────────────────────────────────── */}
        <div className={styles.timeline}>
          {(
            [
              ['uploading',   'Przesyłanie pliku'],
              ['decompiling', 'Dekompilacja'],
              ['analyzing',   'Analiza AI'],
              ['aggregating', 'Wynik zbiorczy'],
              ['complete',    'Raport gotowy'],
            ] as Array<[ScanStatus, string]>
          ).map(([step, label]) => {
            const order = ['uploading', 'decompiling', 'analyzing', 'aggregating', 'complete']
            const isDone = order.indexOf(step) < order.indexOf(status?.status ?? 'uploading') || status?.status === 'complete'
            const isActive = step === status?.status
            return (
              <div
                key={step}
                className={`${styles.timelineItem} ${isDone ? styles.timelineDone : ''} ${isActive ? styles.timelineActive : ''}`}
              >
                <div className={styles.timelineDot} />
                <span className={styles.timelineLabel}>{label}</span>
              </div>
            )
          })}
        </div>

        {isFailed && (
          <div className={styles.errorBox} role="alert">
            <AlertCircle size={16} />
            <span>{error ?? status?.result?.error ?? 'Nieznany błąd'}</span>
          </div>
        )}

        {isFailed && (
          <button className={styles.retryBtn} onClick={() => navigate('/')}>
            Spróbuj z innym plikiem
          </button>
        )}
      </div>
    </div>
  )
}
